import React, { useEffect } from "react";
import {
  OrganizationProvider,
  useOrganization,
} from "./OrganizationContext";
import { MembershipProvider } from "./MembershipContext";
import { PermissionProvider } from "./PermissionContext";
import {
  UserPreferencesProvider,
  useUserPreferences,
} from "./UserPreferencesContext";
import { ConnectivityProvider } from "./ConnectivityContext";
import {
  APPEARANCE_THEME,
  getAppearanceTheme,
} from "../helpers/userPreferences";

/**
 * Applies the user's Appearance preference (Preferences > Appearance) to
 * the document so App.css's :root[data-theme] / prefers-color-scheme
 * rules pick it up. "system" removes the attribute entirely so the OS/
 * browser's own color-scheme media query decides. Renders nothing.
 */
function AppearanceThemeSync() {
  const { preferences } = useUserPreferences();
  const theme = getAppearanceTheme(preferences);

  useEffect(() => {
    if (theme === APPEARANCE_THEME.SYSTEM) {
      document.documentElement.removeAttribute("data-theme");
    } else {
      document.documentElement.setAttribute("data-theme", theme);
    }
  }, [theme]);

  return null;
}

function PermissionBoundary({ actor, directReportIds, children }) {
  const { settings } = useOrganization();

  return (
    <PermissionProvider
      actor={actor}
      orgSettings={settings}
      directReportIds={directReportIds}
    >
      {children}
    </PermissionProvider>
  );
}

/**
 * Gate 3 provider stack:
 * Authenticated User → Membership → Organization → Permissions
 */
export function AppProviders({ actor, directReportIds, children }) {
  return (
    <ConnectivityProvider>
      <MembershipProvider actor={actor}>
        <UserPreferencesProvider actor={actor}>
          <AppearanceThemeSync />
          <OrganizationProvider actor={actor}>
            <PermissionBoundary actor={actor} directReportIds={directReportIds}>
              {children}
            </PermissionBoundary>
          </OrganizationProvider>
        </UserPreferencesProvider>
      </MembershipProvider>
    </ConnectivityProvider>
  );
}
